import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { MANUAL_TENANTS_QUERY_KEY } from '@/modules/shared/query-client/useQueryManualTenants'

export interface Przelewy24TenantSummary {
  tenantId: string
  companyName: string | null
  nip: string | null
  planCode: string | null
  billingMode: string
  subscriptionExpirationDate: string | null
  paid: boolean
  paymentDate: string | null
  permanentlyBlocked: boolean
  lastInvoiceUrl: string | null
}

export interface Przelewy24Transaction {
  id: string
  sessionId: string
  orderId: number | null
  planCode: string | null
  months: number
  amountGrosze: number
  currency: string | null
  status: string
  createdAt: string
  finalizedAt: string | null
  notes: string | null
}

export interface Przelewy24SyncResult {
  activated: boolean
  alreadyProcessed: boolean
  pending: boolean
  failed: boolean
  expiresAt: string | null
  notes: string | null
}

export interface Przelewy24MigrateResult {
  migrated: boolean
  nip: string | null
  migratedChildren: number
  voidedTransactions: number
  unblocked: boolean
}

export const P24_TENANTS_QUERY_KEY = ['admin', 'billing', 'przelewy24', 'tenants'] as const

const transactionsQueryKey = (tenantId: string | null) =>
  ['admin', 'billing', 'przelewy24', 'transactions', tenantId] as const

export function useQueryPrzelewy24Tenants() {
  return useQuery<Przelewy24TenantSummary[]>({
    queryKey: P24_TENANTS_QUERY_KEY,
    queryFn: async () => {
      const res = await axiosInstance.get('/config-api/admin/billing/przelewy24/tenants')
      return res.data
    },
  })
}

export function useQueryPrzelewy24Transactions(tenantId: string | null) {
  return useQuery<Przelewy24Transaction[]>({
    queryKey: transactionsQueryKey(tenantId),
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/config-api/admin/billing/przelewy24/tenants/${tenantId}/transactions`,
      )
      return res.data
    },
    enabled: !!tenantId,
  })
}

export function useMutationSyncPrzelewy24() {
  const queryClient = useQueryClient()
  return useMutation<Przelewy24SyncResult, unknown, string>({
    mutationFn: async (tenantId: string) => {
      const res = await axiosInstance.post(
        `/config-api/admin/billing/przelewy24/tenants/${tenantId}/sync`,
      )
      return res.data
    },
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: P24_TENANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey(tenantId) })
    },
  })
}

export function useMutationRefundPrzelewy24() {
  const queryClient = useQueryClient()
  return useMutation<{ refunded: boolean }, unknown, string>({
    mutationFn: async (tenantId: string) => {
      const res = await axiosInstance.post(
        `/config-api/admin/billing/przelewy24/tenants/${tenantId}/refund`,
      )
      return res.data
    },
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: P24_TENANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey(tenantId) })
    },
  })
}

// Moves a self-service P24 tenant onto Claro-panel (invoiced) billing. The row then
// leaves this tab and shows up under "Rozliczenia ręczne", so both lists are refreshed.
export function useMutationMigratePrzelewy24ToManual() {
  const queryClient = useQueryClient()
  return useMutation<Przelewy24MigrateResult, unknown, string>({
    mutationFn: async (tenantId: string) => {
      const res = await axiosInstance.post(
        `/config-api/admin/billing/przelewy24/tenants/${tenantId}/migrate-to-manual`,
      )
      return res.data
    },
    onSuccess: (_data, tenantId) => {
      queryClient.invalidateQueries({ queryKey: P24_TENANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: MANUAL_TENANTS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: transactionsQueryKey(tenantId) })
    },
  })
}

export function useMutationSetPrzelewy24TenantBlocked() {
  const queryClient = useQueryClient()
  return useMutation<{ permanentlyBlocked: boolean }, unknown, { tenantId: string; blocked: boolean }>({
    mutationFn: async ({ tenantId, blocked }) => {
      const res = await axiosInstance.post(
        `/config-api/admin/billing/przelewy24/tenants/${tenantId}/blocked`,
        { blocked },
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: P24_TENANTS_QUERY_KEY })
    },
  })
}
